import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button, ChevronLeftOutline, ChevronRightOutline, HStack, IconButton, VStack } from '@bonusly/design-system';

import { promptGradientBorderSx } from '../bizy/prompt-gradient';

export interface PromptChipItem {
	prompt: string;
	expandable?: boolean;
}

interface PromptButtonProps {
	fontSize?: 'sm' | 'md';
	prompt: string;
	onSelect: () => void;
	nowrap?: boolean;
	expandable?: boolean;
	fullWidth?: boolean;
}

function PromptButton({ fontSize = 'sm', prompt, onSelect, nowrap, expandable = false, fullWidth = false }: PromptButtonProps) {
	const [expanded, setExpanded] = useState(false);
	const textRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useEffect(() => {
		setExpanded(false);
	}, [prompt]);

	useEffect(() => {
		if (!expandable || expanded) {
			setIsTruncated(false);
			return;
		}

		const element = textRef.current;
		if (!element) return;

		const measure = () => {
			setIsTruncated(element.scrollHeight > element.clientHeight + 1);
		};

		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, [expandable, expanded, prompt]);

	const needsExpandStep = expandable && isTruncated && !expanded;

	const handleClick = () => {
		if (needsExpandStep) {
			setExpanded(true);
			return;
		}
		onSelect();
	};

	return (
		<Button
			variant="outline"
			size="sm"
			borderRadius="12px"
			color="text.softest"
			fontWeight="normal"
			fontSize={fontSize}
			h="auto"
			py={1.5}
			px={3}
			w={fullWidth ? '100%' : undefined}
			maxW={fullWidth ? '100%' : undefined}
			whiteSpace={nowrap ? 'nowrap' : 'normal'}
			textAlign="left"
			alignItems={expandable ? 'flex-start' : undefined}
			justifyContent={expandable ? 'flex-start' : undefined}
			flexShrink={nowrap ? 0 : undefined}
			onClick={handleClick}
			aria-expanded={expandable ? expanded : undefined}
			sx={promptGradientBorderSx}
			_hover={{ color: 'text.default' }}
		>
			<Box
				as="span"
				ref={textRef}
				display="block"
				w={fullWidth ? '100%' : undefined}
				sx={
					expandable && !expanded
						? {
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
							}
						: undefined
				}
			>
				{prompt}
			</Box>
		</Button>
	);
}

const SCROLL_AMOUNT = 200;

interface HorizontalPromptsProps {
	fontSize?: 'sm' | 'md';
	items: PromptChipItem[];
	onSelectPrompt: (prompt: string, index: number) => void;
	/** Use negative horizontal margins to bleed into parent padding (default: true) */
	bleed?: boolean;
	/** Bottom margin on the container (default: 2) */
	mb?: number;
}

export function HorizontalPrompts({ fontSize = 'sm', items, onSelectPrompt, bleed = true, mb: marginBottom = 2 }: HorizontalPromptsProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		updateScrollState();
		const el = scrollRef.current;
		if (!el) return;
		const observer = new ResizeObserver(updateScrollState);
		observer.observe(el);
		return () => observer.disconnect();
	}, [updateScrollState, items]);

	const scroll = useCallback((direction: 'left' | 'right') => {
		scrollRef.current?.scrollBy({ left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT, behavior: 'smooth' });
	}, []);

	return (
		<Box position="relative" mx={bleed ? -2 : 0} mb={marginBottom} overflow={bleed ? undefined : 'hidden'}>
			<Box
				ref={scrollRef}
				overflowX="auto"
				onScroll={updateScrollState}
				sx={{
					maskImage: `linear-gradient(to right, ${canScrollLeft ? 'transparent' : 'black'}, black 16px, black calc(100% - 16px), ${canScrollRight ? 'transparent' : 'black'})`,
					WebkitMaskImage: `linear-gradient(to right, ${canScrollLeft ? 'transparent' : 'black'}, black 16px, black calc(100% - 16px), ${canScrollRight ? 'transparent' : 'black'})`,
					scrollbarWidth: 'none',
					'&::-webkit-scrollbar': { display: 'none' },
				}}
			>
				<HStack spacing={2} px={2} py={1}>
					{items.map((item, index) => (
						<PromptButton
							key={`${index}-${item.prompt}`}
							fontSize={fontSize}
							prompt={item.prompt}
							expandable={item.expandable}
							onSelect={() => onSelectPrompt(item.prompt, index)}
							nowrap
						/>
					))}
				</HStack>
			</Box>

			{canScrollLeft && (
				<IconButton
					aria-label="Scroll left"
					icon={<ChevronLeftOutline boxSize={3} />}
					size="xs"
					variant="outline"
					isRound
					position="absolute"
					left={0}
					top="50%"
					transform="translateY(-50%)"
					bg="bg.surface"
					borderColor="border.soft"
					color="text.soft"
					boxSize={6}
					minW={0}
					p={3}
					opacity={0}
					transition="opacity 0.15s ease-in-out"
					onClick={() => scroll('left')}
					_hover={{ opacity: 1, bg: 'bg.subtle', color: 'text.default' }}
				/>
			)}

			{canScrollRight && (
				<IconButton
					aria-label="Scroll right"
					icon={<ChevronRightOutline boxSize={3} />}
					size="xs"
					variant="outline"
					isRound
					position="absolute"
					right={0}
					top="50%"
					transform="translateY(-50%)"
					bg="bg.surface"
					borderColor="border.soft"
					color="text.soft"
					boxSize={6}
					minW={0}
					p={3}
					opacity={0}
					transition="opacity 0.15s ease-in-out"
					onClick={() => scroll('right')}
					_hover={{ opacity: 1, bg: 'bg.subtle', color: 'text.default' }}
				/>
			)}
		</Box>
	);
}

interface PromptChipsProps {
	fontSize?: 'sm' | 'md';
	items: PromptChipItem[];
	onSelectPrompt: (prompt: string, index: number) => void;
	layout?: 'vertical' | 'horizontal';
}

export function PromptChips({ fontSize = 'sm', items, onSelectPrompt, layout = 'vertical' }: PromptChipsProps) {
	if (items.length === 0) return null;

	if (layout === 'horizontal') {
		return <HorizontalPrompts fontSize={fontSize} items={items} onSelectPrompt={onSelectPrompt} />;
	}

	return (
		<VStack spacing={1} align="stretch" mb={2} w="100%">
			{items.map((item, index) => (
				<PromptButton
					key={`${index}-${item.prompt}`}
					fontSize={fontSize}
					prompt={item.prompt}
					expandable={item.expandable}
					fullWidth={item.expandable}
					onSelect={() => onSelectPrompt(item.prompt, index)}
				/>
			))}
		</VStack>
	);
}
